import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton.tsx";
import ConfirmationPopup from "../../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import ExportButton from "../../../../../../components/common/ExportButton/ExportButton.tsx";
import { BriaImageProps, ImageErrorType } from "../../../../../../components/common/Galleries/BriaImage.tsx";
import LoadingPlaceholder from "../../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import { APPS } from "../../../../../../constants/AppsConstants.ts";
import RouterConstants from "../../../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import { defaultBrandDefinition } from "../../../../../../models/brandDefinition.ts";
import { PlaygroundImage } from "../../../../../../models/image-to-image.ts";
import PlaygroundImageOverlay from "../../../../../../pages/Playground/Images/Overlay/PlaygroundImageOverlay.tsx";
import useDesignEditorUtils from "../../../../../CustomUtils/UseDesignEditor.tsx";
import useTemplateUtils from "../../../../../CustomUtils/UseTemplateUtils.tsx";
import useDesignEditorContext from "../../../../../hooks/useDesignEditorContext.ts";
import CampaignWizardGallery from "../FullScreenViewer/CampaignWizardGallery.tsx";
import styles from "./CampaignResult.module.scss";

const CampaignResult = () => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.popup.campaignResults" });
	const { scenes } = useDesignEditorContext();
	const [isLoading, setIsLoading] = useState(true);
	const [openFullScreen, setOpenFullScreen] = useState(false);
	const [showStartOverConfirmation, setShowStartOverConfirmation] = useState(false);
	const [isTemplateDownloading, setIsTemplateDownloading] = useState(false);
	const { designEditorStore, playgroundStore, campaignStore, brandsDefinitionStore } = useAppStore();
	const playgroundImages = scenes.map((scene) => ({
		id: scene.id,
		url: scene.preview,
		type: APPS.CAMPAIGN,
	})) as (BriaImageProps & PlaygroundImage)[];
	const navigate = useSecureNavigate();
	const { handleDownloadTemplate } = useTemplateUtils();
	const { makeDownloadTemplate } = useDesignEditorUtils();

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsLoading(false);
		}, 1500);

		return () => clearTimeout(timer);
	}, []);

	const resetCampaignSteps = () => {
		designEditorStore.setProperty("activeStep", 0);
		designEditorStore.setProperty("newStep", 0);
		brandsDefinitionStore.setProperty("globalSelectedBrand", defaultBrandDefinition);
		brandsDefinitionStore.setProperty("selectedBrand", defaultBrandDefinition);
		campaignStore.handleSetSelectedWizardTemplate(null);
		designEditorStore.setProperty("imageTypeToNewUrlMap", {});
		designEditorStore.setProperty("backupOfReplacedStaticImagesLayers", []);
		designEditorStore.setProperty("isSceneFullyLoaded", true);
		designEditorStore.setProperty("campaignUpdatedTemplate", undefined);
		designEditorStore.setProperty("hideCampaignResultContent", true);
		designEditorStore.setProperty("isTemplateEmptyState", true);
		campaignStore.setProperty("selectedWizardTemplate", null);
		campaignStore.setProperty("selectedTemplate", null);
		designEditorStore.setProperty("editorStepperDisabledSteps", undefined);
	};

	const openInAdsEditor = () => {
		campaignStore.setProperty("brandToDisplayInEditor", brandsDefinitionStore.globalSelectedBrand);
		campaignStore.setProperty("isFromAdsGenerator", true);
		campaignStore.setProperty("isLoadingScenesInAdsEditor", true);
		navigate(RouterConstants.ADS_EDITOR.fullPath);
	};

	const onDownload = async () => {
		if (designEditorStore.campaignUpdatedTemplate) {
			setIsTemplateDownloading(true);
			await handleDownloadTemplate(designEditorStore.campaignUpdatedTemplate);
			setIsTemplateDownloading(false);
		}
	};

	const onJsonExport = async () => {
		let templateName;
		if (designEditorStore.campaignUpdatedTemplate) {
			templateName = `${"Template"}_${designEditorStore.campaignUpdatedTemplate.name}`;
		}
		await makeDownloadTemplate(templateName);
	};

	return (
		<>
			<Box className={styles.header}>
				<Typography className={styles.typography}>
					{t("template")}:{" "}
					<Box component="span" fontWeight="bold">
						{campaignStore.selectedWizardTemplate?.name}
					</Box>
					{campaignStore?.selectedWizardTemplate?.is_branded_template && (
						<>
							{" | "}
							{t("brand")}:{" "}
							<Box component="span" fontWeight="bold">
								{brandsDefinitionStore.selectedBrand.name}
							</Box>
						</>
					)}
				</Typography>
				<Box className={styles.buttonGroup}>
					<BriaButton
						onClick={() => setShowStartOverConfirmation(true)}
						buttonType="secondaryMedium"
						size={"small"}
					>
						{t("startOver")}
					</BriaButton>
					<BriaButton
						onClick={openInAdsEditor}
						buttonType="secondaryMedium"
						size={"small"}
						disabled={designEditorStore.campaignUpdatedTemplate === undefined}
					>
						{t("openInAdsEditor")}
					</BriaButton>
					<ExportButton
						buttonType={"primaryMedium"}
						disableExportPopup={true}
						exportJson={false}
						onDownload={onDownload}
						onIframeSave={onDownload}
						onJsonExport={onJsonExport}
						loading={isTemplateDownloading}
						disabled={designEditorStore.campaignUpdatedTemplate === undefined || isTemplateDownloading}
						label={t("export.label")}
						pngLabel={t("export.pngLabel")}
						jsonLabel={t("export.jsonLabel")}
					/>
				</Box>
			</Box>
			<Box className={styles.campaignGenerationResult}>
				<LoadingPlaceholder className={styles.loaderStyle} isLoading={isLoading}>
					<Box className={styles.adsContainer}>
						<CampaignWizardGallery
							images={playgroundImages.map((image) => ({
								ImageOverlay: <PlaygroundImageOverlay {...{ image }} />,
								displayOverlay: "hover",
								aspectRatio: undefined,
								selectable: false,
								htmlJsonData: image.id,
								async: false,
								onSuccessPulling: () => playgroundStore.onSuccessResult(image),
								onErrorPulling: (errorType: ImageErrorType) =>
									playgroundStore.onErrorResult(image, errorType),
								fullScreenProps: {
									fileName: playgroundStore.getFileName(image),
								},
								...image,
							}))}
							fullScreenProps={{
								open: openFullScreen,
								onClose: () => setOpenFullScreen(false),
								displayMode: "scroll",
							}}
							className={styles.gallery}
						/>
					</Box>
				</LoadingPlaceholder>
			</Box>
			{/* Confirmation popup for starting over*/}
			<ConfirmationPopup
				onClose={() => setShowStartOverConfirmation(false)}
				title={t("popup.startOverHeader")}
				description={t("popup.startOverMessage")}
				confirmButtonText={t("popup.startOverConfirmButton")}
				open={showStartOverConfirmation}
				onClick={resetCampaignSteps}
			/>
		</>
	);
};

const ObservedComponent = observer(CampaignResult);
export default ObservedComponent;
